import React, { useEffect, useState, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Building2, Plus, X, ChevronDown, ChevronUp, Pencil, Trash2 } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

// ── Constants ──────────────────────────────────────────────────────────────────

const COMPETENCY_TYPES = ['Handledare', 'OCN-bedömare', 'Vägledare', 'Coach', 'Övrig'];

const STATUS_CONFIG = {
  Aktiv:   { label: 'Aktiv',   cls: 'bg-success/20 text-success' },
  Inaktiv: { label: 'Inaktiv', cls: 'bg-muted text-muted-foreground' },
};

const selectCls =
  'h-9 w-full px-3 border border-input rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50';

// Maps form field → catalog standardvärden key
const STANDARD_FIELD_MAP = {
  activities:    'standard_aktivitet',
  outcomes:      'standard_utfall',
  target_groups: 'standard_målgrupp',
  collaborations: 'standard_samverkan',
};

// Display labels for the multi-select catalog fields
const MULTI_FIELD_LABELS = {
  activities:    'Aktivitet',
  outcomes:      'Utfall',
  target_groups: 'Målgrupp',
  collaborations: 'Samverkan',
};

// ── Helper: build empty form state ─────────────────────────────────────────────

const emptyForm = () => ({
  area: '',
  category: '',
  intervention: '',
  intervention_type: '',
  status: 'Aktiv',
  activities: [],
  outcomes: [],
  target_groups: [],
  collaborations: [],
  capacity: [],
  competencies: [],
});

// ── Sub-components ─────────────────────────────────────────────────────────────

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.Inaktiv;
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
};

// ── Catalog lookup helpers ─────────────────────────────────────────────────────

const areaEntry = (catalog, area) =>
  catalog.find((c) => (c.område || c.omrade) === area);

const getAreas = (catalog) => catalog.map((c) => c.område || c.omrade);

const getCategories = (catalog, area) => {
  const entry = areaEntry(catalog, area);
  return entry ? entry.kategorier.map((k) => k.kategori) : [];
};

const getInterventions = (catalog, area, category) => {
  const entry = areaEntry(catalog, area);
  if (!entry) return [];
  const kat = entry.kategorier.find((k) => k.kategori === category);
  return kat ? kat.insatser : [];
};

const findInsats = (catalog, area, category, intervention) => {
  const insatser = getInterventions(catalog, area, category);
  return insatser.find((i) => i.insats === intervention) || null;
};

// Multi-select toggle: add value if missing, remove if present
const toggleValue = (arr, value) =>
  arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];

// ── Inline form ────────────────────────────────────────────────────────────────

const VerksamhetForm = ({ catalog, initial, onSave, onCancel, isEdit }) => {
  const [form, setForm]       = useState(initial || emptyForm());
  const [errors, setErrors]   = useState({});
  const [submitting, setSub]  = useState(false);

  // Cascade: when area changes, reset category, intervention and all autofilled values
  const handleArea = (area) => {
    setForm((f) => ({
      ...f,
      area,
      category: '',
      intervention: '',
      intervention_type: '',
      activities: [],
      outcomes: [],
      target_groups: [],
      collaborations: [],
    }));
  };

  const handleCategory = (category) => {
    setForm((f) => ({
      ...f,
      category,
      intervention: '',
      intervention_type: '',
      activities: [],
      outcomes: [],
      target_groups: [],
      collaborations: [],
    }));
  };

  const handleIntervention = (intervention) => {
    const insats = findInsats(catalog, form.area, form.category, intervention);
    if (!insats) {
      setForm((f) => ({
        ...f,
        intervention,
        intervention_type: '',
        activities: [],
        outcomes: [],
        target_groups: [],
        collaborations: [],
      }));
      return;
    }
    // Autofyll: insatstyp låses, standardvärden förifylls som valda värden
    setForm((f) => ({
      ...f,
      intervention,
      intervention_type: insats.insatstyp,
      activities:     insats.standard_aktivitet ? [insats.standard_aktivitet] : [],
      outcomes:       insats.standard_utfall    ? [insats.standard_utfall]    : [],
      target_groups:  insats.standard_målgrupp  ? [insats.standard_målgrupp]  : [],
      collaborations: insats.standard_samverkan ? [insats.standard_samverkan] : [],
    }));
  };

  const handleToggleMulti = (field, value) => {
    setForm((f) => ({ ...f, [field]: toggleValue(f[field] || [], value) }));
  };

  // Capacity rows
  const addCapacity = () => {
    if (form.capacity.length >= 10) return;
    setForm((f) => ({ ...f, capacity: [...f.capacity, { unit_name: '', capacity_count: 1 }] }));
  };

  const updateCapacity = (idx, field, value) => {
    setForm((f) => {
      const rows = [...f.capacity];
      rows[idx] = { ...rows[idx], [field]: field === 'capacity_count' ? Number(value) : value };
      return { ...f, capacity: rows };
    });
  };

  const removeCapacity = (idx) => {
    setForm((f) => ({ ...f, capacity: f.capacity.filter((_, i) => i !== idx) }));
  };

  // Competency rows
  const addCompetency = () => {
    if (form.competencies.length >= 10) return;
    setForm((f) => ({
      ...f,
      competencies: [...f.competencies, { competency_type: COMPETENCY_TYPES[0], required_count: 1 }],
    }));
  };

  const updateCompetency = (idx, field, value) => {
    setForm((f) => {
      const rows = [...f.competencies];
      rows[idx] = { ...rows[idx], [field]: field === 'required_count' ? Number(value) : value };
      return { ...f, competencies: rows };
    });
  };

  const removeCompetency = (idx) => {
    setForm((f) => ({ ...f, competencies: f.competencies.filter((_, i) => i !== idx) }));
  };

  const validate = () => {
    const errs = {};
    if (!form.area)         errs.area         = 'Välj område';
    if (!form.category)     errs.category     = 'Välj kategori';
    if (!form.intervention) errs.intervention = 'Välj insats';
    form.capacity.forEach((r, i) => {
      if (!r.unit_name.trim()) errs[`cap_unit_${i}`] = 'Enhet krävs';
      if (r.capacity_count < 1) errs[`cap_count_${i}`] = 'Minst 1';
    });
    const units = form.capacity.map((r) => r.unit_name.trim());
    if (units.length !== new Set(units).size) errs.capacity_dup = 'Duplicerade enheter';
    form.competencies.forEach((r, i) => {
      if (r.required_count < 1) errs[`comp_count_${i}`] = 'Minst 1';
    });
    const ctypes = form.competencies.map((r) => r.competency_type);
    if (ctypes.length !== new Set(ctypes).size) errs.comp_dup = 'Duplicerade kompetenstyper';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSub(true);
    try {
      const payload = {
        area:           form.area,
        category:       form.category,
        intervention:   form.intervention,
        status:         form.status,
        activities:     form.activities || [],
        outcomes:       form.outcomes || [],
        target_groups:  form.target_groups || [],
        collaborations: form.collaborations || [],
        capacity:       form.capacity,
        competencies:   form.competencies,
      };
      if (isEdit) {
        const res = await api.patch(`/org/verksamheter/${initial.id}`, payload);
        onSave(res.data);
        toast.success('Verksamhet uppdaterad!');
      } else {
        const res = await api.post('/org/verksamheter', payload);
        onSave(res.data);
        toast.success('Verksamhet skapad!');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Något gick fel');
    } finally {
      setSub(false);
    }
  };

  const areas       = getAreas(catalog);
  const categories  = getCategories(catalog, form.area);
  const insatser    = getInterventions(catalog, form.area, form.category);
  const selectedInsats = findInsats(catalog, form.area, form.category, form.intervention);

  // Per-field allowed values for multi-select (standardvärden from catalog)
  const allowedValuesFor = (field) => {
    if (!selectedInsats) return [];
    const v = selectedInsats[STANDARD_FIELD_MAP[field]];
    return v ? [v] : [];
  };

  return (
    <Card data-testid={isEdit ? `edit-form-${initial?.id}` : 'create-form'}>
      <CardContent className="p-6">
        <form onSubmit={handleSubmit} className="space-y-6" noValidate>

          {/* ── Section 1: Klassificering ── */}
          <div>
            <h3 className="font-heading font-bold text-base mb-3">Klassificering</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">

              {/* Område */}
              <div>
                <Label htmlFor={`area-${isEdit ? initial?.id : 'new'}`}>Område</Label>
                <select
                  id={`area-${isEdit ? initial?.id : 'new'}`}
                  data-testid="select-area"
                  className={selectCls}
                  value={form.area}
                  onChange={(e) => handleArea(e.target.value)}
                >
                  <option value="">— välj —</option>
                  {areas.map((a) => (
                    <option key={a} value={a}>{a}</option>
                  ))}
                </select>
                {errors.area && <p className="text-destructive text-xs mt-1">{errors.area}</p>}
              </div>

              {/* Kategori */}
              <div>
                <Label htmlFor={`cat-${isEdit ? initial?.id : 'new'}`}>Kategori</Label>
                <select
                  id={`cat-${isEdit ? initial?.id : 'new'}`}
                  data-testid="select-category"
                  className={selectCls}
                  value={form.category}
                  onChange={(e) => handleCategory(e.target.value)}
                  disabled={!form.area}
                >
                  <option value="">— välj —</option>
                  {categories.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                {errors.category && <p className="text-destructive text-xs mt-1">{errors.category}</p>}
              </div>

              {/* Insats */}
              <div>
                <Label htmlFor={`ins-${isEdit ? initial?.id : 'new'}`}>Insats</Label>
                <select
                  id={`ins-${isEdit ? initial?.id : 'new'}`}
                  data-testid="select-intervention"
                  className={selectCls}
                  value={form.intervention}
                  onChange={(e) => handleIntervention(e.target.value)}
                  disabled={!form.category}
                >
                  <option value="">— välj —</option>
                  {insatser.map((i) => (
                    <option key={i.insats} value={i.insats}>{i.insats}</option>
                  ))}
                </select>
                {errors.intervention && <p className="text-destructive text-xs mt-1">{errors.intervention}</p>}
              </div>
            </div>

            {/* Insatstyp — autofylld + låst */}
            <div className="mt-4">
              <Label htmlFor={`insatstyp-${isEdit ? initial?.id : 'new'}`}>Insatstyp</Label>
              <Input
                id={`insatstyp-${isEdit ? initial?.id : 'new'}`}
                data-testid="insatstyp-input"
                value={form.intervention_type}
                readOnly
                disabled
                placeholder="Autofylls när Insats väljs"
                className="max-w-md"
              />
              {form.intervention_type && (
                <p className="text-xs text-muted-foreground mt-1" data-testid="insatstyp-display">
                  Insatstyp: <span className="font-semibold">{form.intervention_type}</span>
                </p>
              )}
            </div>
          </div>

          {/* ── Section 2: Standardvärden (multi-select från katalog) ── */}
          {selectedInsats && (
            <div className="space-y-4" data-testid="standard-values-section">
              <h3 className="font-heading font-bold text-base">Standardvärden</h3>
              {['activities', 'outcomes', 'target_groups', 'collaborations'].map((field) => {
                const allowed  = allowedValuesFor(field);
                const selected = form[field] || [];
                return (
                  <div key={field} data-testid={`multi-${field}`}>
                    <Label>{MULTI_FIELD_LABELS[field]}</Label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {allowed.length === 0 && (
                        <p className="text-xs text-muted-foreground">Inga katalogvärden för denna insats</p>
                      )}
                      {allowed.map((val) => {
                        const isOn = selected.includes(val);
                        return (
                          <button
                            type="button"
                            key={val}
                            data-testid={`multi-${field}-option-${val}`}
                            onClick={() => handleToggleMulti(field, val)}
                            className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                              isOn
                                ? 'bg-primary/20 border-primary text-foreground font-semibold'
                                : 'bg-background border-input text-muted-foreground'
                            }`}
                            aria-pressed={isOn}
                          >
                            {val}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Section 3: Status ── */}
          <div>
            <Label htmlFor={`status-${isEdit ? initial?.id : 'new'}`}>Status</Label>
            <select
              id={`status-${isEdit ? initial?.id : 'new'}`}
              data-testid="select-status"
              className={`${selectCls} max-w-[200px]`}
              value={form.status}
              onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
            >
              <option value="Aktiv">Aktiv</option>
              <option value="Inaktiv">Inaktiv</option>
            </select>
          </div>

          {/* ── Section 4: Kapacitet ── */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-heading font-bold text-base">Kapacitet</h3>
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="add-capacity-row"
                onClick={addCapacity}
                disabled={form.capacity.length >= 10}
              >
                <Plus size={14} className="mr-1" /> Lägg till enhet
              </Button>
            </div>
            {errors.capacity_dup && (
              <p className="text-destructive text-xs mb-2">{errors.capacity_dup}</p>
            )}
            {form.capacity.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="capacity-empty">
                Inga kapacitetsrader tillagda.
              </p>
            ) : (
              <div className="space-y-2">
                {form.capacity.map((row, idx) => (
                  <div key={idx} className="flex gap-2 items-start" data-testid={`capacity-row-${idx}`}>
                    <div className="flex-1">
                      <Input
                        placeholder="Enhetens namn"
                        data-testid={`capacity-unit-${idx}`}
                        value={row.unit_name}
                        onChange={(e) => updateCapacity(idx, 'unit_name', e.target.value)}
                      />
                      {errors[`cap_unit_${idx}`] && (
                        <p className="text-destructive text-xs mt-1">{errors[`cap_unit_${idx}`]}</p>
                      )}
                    </div>
                    <div className="w-28">
                      <Input
                        type="number"
                        min={1}
                        placeholder="Platser"
                        data-testid={`capacity-count-${idx}`}
                        value={row.capacity_count}
                        onChange={(e) => updateCapacity(idx, 'capacity_count', e.target.value)}
                      />
                      {errors[`cap_count_${idx}`] && (
                        <p className="text-destructive text-xs mt-1">{errors[`cap_count_${idx}`]}</p>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      data-testid={`remove-capacity-${idx}`}
                      onClick={() => removeCapacity(idx)}
                      className="text-muted-foreground hover:text-destructive mt-0.5"
                    >
                      <X size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Section 5: Kompetenskrav ── */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-heading font-bold text-base">Kompetenskrav</h3>
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="add-competency-row"
                onClick={addCompetency}
                disabled={form.competencies.length >= 10}
              >
                <Plus size={14} className="mr-1" /> Lägg till kompetens
              </Button>
            </div>
            {errors.comp_dup && (
              <p className="text-destructive text-xs mb-2">{errors.comp_dup}</p>
            )}
            {form.competencies.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="competency-empty">
                Inga kompetenskrav tillagda.
              </p>
            ) : (
              <div className="space-y-2">
                {form.competencies.map((row, idx) => (
                  <div key={idx} className="flex gap-2 items-start" data-testid={`competency-row-${idx}`}>
                    <div className="flex-1">
                      <select
                        data-testid={`competency-type-${idx}`}
                        className={selectCls}
                        value={row.competency_type}
                        onChange={(e) => updateCompetency(idx, 'competency_type', e.target.value)}
                      >
                        {COMPETENCY_TYPES.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                    <div className="w-28">
                      <Input
                        type="number"
                        min={1}
                        placeholder="Antal"
                        data-testid={`competency-count-${idx}`}
                        value={row.required_count}
                        onChange={(e) => updateCompetency(idx, 'required_count', e.target.value)}
                      />
                      {errors[`comp_count_${idx}`] && (
                        <p className="text-destructive text-xs mt-1">{errors[`comp_count_${idx}`]}</p>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      data-testid={`remove-competency-${idx}`}
                      onClick={() => removeCompetency(idx)}
                      className="text-muted-foreground hover:text-destructive mt-0.5"
                    >
                      <X size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Actions ── */}
          <div className="flex gap-3">
            <Button
              type="submit"
              disabled={submitting}
              data-testid="submit-form"
              className="rounded-full font-bold"
              style={{ background: '#F9CF3F', color: '#333333' }}
            >
              {submitting ? 'Sparar...' : isEdit ? 'Spara ändringar' : 'Skapa verksamhet'}
            </Button>
            <Button type="button" variant="ghost" onClick={onCancel} data-testid="cancel-form">
              Avbryt
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

// ── Row card (collapsed + expanded edit) ───────────────────────────────────────

const VerksamhetRow = ({ item, catalog, onUpdated, onDeleted }) => {
  const [expanded, setExpanded] = useState(false);

  const handleDelete = async () => {
    if (!window.confirm('Ta bort denna verksamhet? Åtgärden går inte att ångra.')) return;
    try {
      await api.delete(`/org/verksamheter/${item.id}`);
      toast.success('Verksamhet borttagen!');
      onDeleted(item.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte ta bort');
    }
  };

  const initialForm = {
    id:                item.id,
    area:              item.area,
    category:          item.category,
    intervention:      item.intervention,
    intervention_type: item.intervention_type,
    status:            item.status,
    activities:        item.activities || [],
    outcomes:          item.outcomes || [],
    target_groups:     item.target_groups || [],
    collaborations:    item.collaborations || [],
    capacity:          (item.capacity || []).map((c) => ({
      unit_name:      c.unit_name,
      capacity_count: c.capacity_count,
    })),
    competencies: (item.competencies || []).map((c) => ({
      competency_type: c.competency_type,
      required_count:  c.required_count,
    })),
  };

  return (
    <div data-testid={`verksamhet-row-${item.id}`}>
      {/* Collapsed header */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-sm truncate" data-testid={`row-intervention-${item.id}`}>
                  {item.intervention}
                </span>
                <StatusBadge status={item.status} />
              </div>
              <p className="text-xs text-muted-foreground mt-0.5 truncate">
                {item.area} › {item.category}
              </p>
              <p className="text-xs text-muted-foreground">
                Insatstyp: {item.intervention_type}
                {item.capacity?.length > 0 && (
                  <> · {item.capacity.reduce((s, c) => s + c.capacity_count, 0)} platser</>
                )}
              </p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button
                variant="ghost"
                size="icon"
                data-testid={`edit-btn-${item.id}`}
                onClick={() => setExpanded((v) => !v)}
                aria-expanded={expanded}
                aria-label="Redigera"
              >
                {expanded ? <ChevronUp size={16} /> : <Pencil size={16} />}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                data-testid={`delete-btn-${item.id}`}
                onClick={handleDelete}
                className="text-muted-foreground hover:text-destructive"
                aria-label="Ta bort"
              >
                <Trash2 size={16} />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Expanded edit form */}
      {expanded && (
        <div className="mt-2 pl-4 border-l-2 border-primary/30">
          <VerksamhetForm
            catalog={catalog}
            initial={initialForm}
            isEdit
            onSave={(updated) => {
              onUpdated(updated);
              setExpanded(false);
            }}
            onCancel={() => setExpanded(false)}
          />
        </div>
      )}
    </div>
  );
};

// ── Page ───────────────────────────────────────────────────────────────────────

const VerksamheterPage = () => {
  const [verksamheter, setVerksamheter] = useState([]);
  const [catalog, setCatalog]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [showCreate, setShowCreate]     = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [vRes, cRes] = await Promise.all([
        api.get('/org/verksamheter'),
        api.get('/verksamheter/catalog'),
      ]);
      setVerksamheter(vRes.data || []);
      setCatalog(cRes.data || []);
    } catch {
      toast.error('Kunde inte hämta verksamheter');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCreated = (item) => {
    setVerksamheter((prev) => [item, ...prev]);
    setShowCreate(false);
  };

  const handleUpdated = (updated) => {
    setVerksamheter((prev) => prev.map((v) => (v.id === updated.id ? updated : v)));
  };

  const handleDeleted = (id) => {
    setVerksamheter((prev) => prev.filter((v) => v.id !== id));
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="verksamheter-page">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-heading font-black tracking-tight">Verksamheter</h1>
            <p className="text-muted-foreground mt-1">
              Hantera organisationens arbetsmarknadsnära insatser
            </p>
          </div>
          {!showCreate && (
            <Button
              data-testid="create-verksamhet-btn"
              onClick={() => setShowCreate(true)}
              className="rounded-full font-bold"
              style={{ background: '#F9CF3F', color: '#333333' }}
            >
              <Plus size={18} className="mr-2" /> Ny verksamhet
            </Button>
          )}
        </div>

        {/* Inline create form */}
        {showCreate && (
          <VerksamhetForm
            catalog={catalog}
            isEdit={false}
            onSave={handleCreated}
            onCancel={() => setShowCreate(false)}
          />
        )}

        {/* List */}
        {loading ? (
          <div className="space-y-3" data-testid="loading-skeleton">
            {[0, 1, 2].map((i) => (
              <Card key={i}>
                <CardContent className="p-4">
                  <div className="h-4 bg-muted rounded animate-pulse w-1/3 mb-2" />
                  <div className="h-3 bg-muted rounded animate-pulse w-1/2" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : verksamheter.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center" data-testid="empty-state">
              <Building2 size={40} className="mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground font-medium">Inga verksamheter ännu</p>
              <p className="text-sm text-muted-foreground mt-1">
                Klicka på "Ny verksamhet" för att komma igång.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="verksamheter-list">
            {verksamheter.map((v) => (
              <VerksamhetRow
                key={v.id}
                item={v}
                catalog={catalog}
                onUpdated={handleUpdated}
                onDeleted={handleDeleted}
              />
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default VerksamheterPage;
